import React, { createContext, useContext, useState, useEffect, useMemo } from 'react';
import { ThemeProvider as MuiThemeProvider } from '@mui/material/styles';
import CssBaseline from '@mui/material/CssBaseline';
import { buildTheme, ACCENT_PRESETS } from 'src/config/theme';

const AppearanceContext = createContext(null);

const STORAGE_KEYS = {
  MODE: 'STAFF_APPEARANCE_MODE',
  ACCENT: 'STAFF_APPEARANCE_ACCENT',
  DENSITY: 'STAFF_APPEARANCE_DENSITY',
};

export const AppearanceProvider = ({ children }) => {
  // Mode: 'light' | 'dark' | 'system'
  const [mode, setModeState] = useState(() => {
    return localStorage.getItem(STORAGE_KEYS.MODE) || 'light';
  });

  // Accent: 'indigo' | 'teal' | 'ocean' | 'emerald' | 'purple' | 'rose'
  const [accentKey, setAccentKeyState] = useState(() => {
    const saved = localStorage.getItem(STORAGE_KEYS.ACCENT);
    return ACCENT_PRESETS[saved] ? saved : 'indigo';
  });

  // Density: 'comfortable' | 'compact'
  const [density, setDensityState] = useState(() => {
    return localStorage.getItem(STORAGE_KEYS.DENSITY) || 'comfortable';
  });

  // Modal visibility
  const [openAppearanceModal, setOpenAppearanceModal] = useState(false);

  // System dark preference listener
  const [systemPrefersDark, setSystemPrefersDark] = useState(() => {
    if (typeof window !== 'undefined' && window.matchMedia) {
      return window.matchMedia('(prefers-color-scheme: dark)').matches;
    }
    return false;
  });

  useEffect(() => {
    if (typeof window === 'undefined' || !window.matchMedia) return;
    const mediaQuery = window.matchMedia('(prefers-color-scheme: dark)');
    const handleChange = (e) => setSystemPrefersDark(e.matches);
    mediaQuery.addEventListener('change', handleChange);
    return () => mediaQuery.removeEventListener('change', handleChange);
  }, []);

  const isDark = useMemo(() => {
    if (mode === 'system') return systemPrefersDark;
    return mode === 'dark';
  }, [mode, systemPrefersDark]);

  const setMode = (newMode) => {
    setModeState(newMode);
    localStorage.setItem(STORAGE_KEYS.MODE, newMode);
  };

  const setAccentKey = (newAccent) => {
    if (ACCENT_PRESETS[newAccent]) {
      setAccentKeyState(newAccent);
      localStorage.setItem(STORAGE_KEYS.ACCENT, newAccent);
    }
  };

  const setDensity = (newDensity) => {
    setDensityState(newDensity);
    localStorage.setItem(STORAGE_KEYS.DENSITY, newDensity);
  };

  const toggleDarkMode = () => {
    if (mode === 'light') {
      setMode('dark');
    } else {
      setMode('light');
    }
  };

  const resetAppearance = () => {
    setMode('light');
    setAccentKey('indigo');
    setDensity('comfortable');
  };

  // Sync DOM data-theme attribute & background for global styling
  useEffect(() => {
    const root = document.documentElement;
    const effectiveTheme = isDark ? 'dark' : 'light';
    root.setAttribute('data-theme', effectiveTheme);
    root.setAttribute('data-density', density);
    document.body.style.backgroundColor = isDark ? '#0F172A' : '#F8FAFC';
    document.body.style.color = isDark ? '#F8FAFC' : '#0F172A';
  }, [isDark, density]);

  // Construct dynamic MUI theme
  const theme = useMemo(() => {
    return buildTheme({
      mode: isDark ? 'dark' : 'light',
      accentKey,
      density,
    });
  }, [isDark, accentKey, density]);

  const value = {
    mode,
    setMode,
    accentKey,
    setAccentKey,
    density,
    setDensity,
    isDark,
    toggleDarkMode,
    resetAppearance,
    openAppearanceModal,
    setOpenAppearanceModal,
    accentPresets: ACCENT_PRESETS,
  };

  return (
    <AppearanceContext.Provider value={value}>
      <MuiThemeProvider theme={theme}>
        <CssBaseline />
        {children}
      </MuiThemeProvider>
    </AppearanceContext.Provider>
  );
};

export const useAppearance = () => {
  const context = useContext(AppearanceContext);
  if (!context) {
    throw new Error('useAppearance must be used within an AppearanceProvider');
  }
  return context;
};

export default AppearanceContext;
